import { ViewConfig } from '../../../models/view-config.interface';
import { ConsigneeDeliveryNotesPaginatedService } from '@app/consignee/services/consignee-delivery-notes-paginated.service';
import { AppStore } from '@app/stores/appStore';
import { ConsigneeViewActionsService } from '../consignee-view-actions.service';
import { TableActionsConfig } from '@app/shared/components/table-cell-components/table-actions.component';
import { ColumnCreator } from '../column-creator';
import {
    DeliveryNoteData,
    TableRowData,
} from '@app/shared/models/table-data-types';

export interface ConsigneeDeliveryNotesConfigDeps {
    consigneeDeliveryNotesPaginatedService: ConsigneeDeliveryNotesPaginatedService;
    actionsService: ConsigneeViewActionsService;
    appStore: InstanceType<typeof AppStore>;
}

export class ConsigneeDeliveryNotesConfig {
    static create(deps: ConsigneeDeliveryNotesConfigDeps): ViewConfig {
        return {
            id: 'consignee-deliverynotes',
            name: 'Delivery Notes',
            translationKey: 'OUTGOING-GOODS.TOGGLER.DELIVERY-NOTES',
            type: 'deliverynotes',
            userRole: 'consignee',
            apiConfig: {
                loadData: () => {
                    const orgSiteKey =
                        deps.appStore.currentOrganizationSiteKey();
                    if (!orgSiteKey) {
                        return Promise.resolve();
                    }
                    return deps.consigneeDeliveryNotesPaginatedService.loadConsigneeDeliveryNotesPaginated(
                        orgSiteKey
                    );
                },
            },
            tableConfig: {
                columns: [
                    ColumnCreator.select({ visible: false }),
                    ColumnCreator.actions({
                        width: '200px',
                        configuration: {
                            type: 'deliveryNote',
                            showView: true,
                            showDownload: true,
                            showAddNotes: true,

                            // Endpoints
                            viewEndpoint: (item: TableRowData) =>
                                deps.actionsService.viewDeliveryNote(
                                    item as DeliveryNoteData
                                ),
                            downloadEndpoint: (item: TableRowData) =>
                                deps.actionsService.downloadDeliveryNote(
                                    item as DeliveryNoteData
                                ),
                            addNotesEndpoint: (item: TableRowData) =>
                                deps.actionsService.addFineDiscrepancy(
                                    item as DeliveryNoteData
                                ),

                            // Configuration
                            organizationSiteKey:
                                deps.appStore.currentOrganizationSiteKey(),
                            context: 'consignee',

                            // Visibility conditions
                            isEditVisible: () => false,
                            isDeleteVisible: () => false,
                            isAddNotesVisible: (item: TableRowData) =>
                                (item as DeliveryNoteData).checkinDate &&
                                !(item as DeliveryNoteData).fineDiscrepancies,
                        } as TableActionsConfig,
                    }),
                    ColumnCreator.deliveryNoteNumber(),
                    ColumnCreator.referenceNumber({
                        orgType: 'consignee',
                        readOnly: true,
                        organizationSiteKeyResolver: row =>
                            (row as DeliveryNoteData).consigneeKey || '',
                    }),
                    ColumnCreator.orderNumber(),
                    ColumnCreator.deliveryNoteBundleNumber(),
                    ColumnCreator.tourNumber(),
                    ColumnCreator.originalConsignor(),
                    ColumnCreator.originalConsignorGln(),
                    ColumnCreator.consignorName(),
                    ColumnCreator.carrierName({
                        header: 'INCOMING-GOODS.TABLE.CARRIER-NAME',
                    }),
                    ColumnCreator.dateShipped(),
                    ColumnCreator.dateAnnounced(),
                    ColumnCreator.dateReceived(),
                    ColumnCreator.carrierLicensePlate({
                        plateType: 'incoming',
                    }),
                    ColumnCreator.dnStatus(),
                    ColumnCreator.discrepancies(),
                    ColumnCreator.palletTickets(),
                ],
                features: {
                    sortable: true,
                    filterable: true,
                    paginated: true,
                    selectable: false,
                    expandable: false,
                },
            },
            filterConfig: {
                filterKeys: [
                    {
                        keyName: 'number',
                        label: 'OUTGOING-GOODS.DELIVERY-NOTE-LIST-VIEW.TABLE-HEADER.DELIVERY-NOTE-NUMBER',
                        translationKey:
                            'OUTGOING-GOODS.DELIVERY-NOTE-LIST-VIEW.TABLE-HEADER.DELIVERY-NOTE-NUMBER',
                    },
                    {
                        keyName: 'properties',
                        label: 'OUTGOING-GOODS.FOLDER-VIEW.TABLE-HEADER.REFERENCE-NUMBER',
                        translationKey:
                            'OUTGOING-GOODS.FOLDER-VIEW.TABLE-HEADER.REFERENCE-NUMBER',
                    },
                    {
                        keyName: 'orderNumber',
                        label: 'OUTGOING-GOODS.DELIVERY-NOTE-LIST-VIEW.TABLE-HEADER.ORDER-NO',
                        translationKey:
                            'OUTGOING-GOODS.DELIVERY-NOTE-LIST-VIEW.TABLE-HEADER.ORDER-NO',
                    },
                    {
                        keyName: 'deliveryNoteBundleNumber',
                        label: 'OUTGOING-GOODS.FOLDER-VIEW.TABLE-HEADER.DELIVERY-NO',
                        translationKey:
                            'OUTGOING-GOODS.FOLDER-VIEW.TABLE-HEADER.DELIVERY-NO',
                    },
                    {
                        keyName: 'tourNumber',
                        label: 'SPEDITEUR.TABLE.HEADERS.TOUR-KEY',
                        translationKey: 'SPEDITEUR.TABLE.HEADERS.TOUR-KEY',
                    },
                    {
                        keyName: 'originalConsignor',
                        label: 'GLOBAL.ORIGINAL_CONSIGNOR',
                        translationKey: 'GLOBAL.ORIGINAL_CONSIGNOR',
                    },
                    {
                        keyName: 'originalConsignorGln',
                        label: 'GLOBAL.GLN',
                        translationKey: 'GLOBAL.GLN',
                    },
                    {
                        keyName: 'consignorName',
                        label: 'OUTGOING-GOODS.DELIVERY-NOTE-LIST-VIEW.TABLE-HEADER.CONSIGNOR-NAME',
                        translationKey:
                            'OUTGOING-GOODS.DELIVERY-NOTE-LIST-VIEW.TABLE-HEADER.CONSIGNOR-NAME',
                    },
                    {
                        keyName: 'carrierName',
                        label: 'OUTGOING-GOODS.DELIVERY-NOTE-LIST-VIEW.TABLE-HEADER.CARRIER-NAME',
                        translationKey:
                            'OUTGOING-GOODS.DELIVERY-NOTE-LIST-VIEW.TABLE-HEADER.CARRIER-NAME',
                    },
                    {
                        keyName: 'checkoutDate',
                        label: 'OUTGOING-GOODS.DELIVERY-NOTE-LIST-VIEW.TABLE-HEADER.SENT-DATE',
                        translationKey:
                            'OUTGOING-GOODS.DELIVERY-NOTE-LIST-VIEW.TABLE-HEADER.SENT-DATE',
                    },
                    {
                        keyName: 'dateAnnounced',
                        label: 'OUTGOING-GOODS.DELIVERY-NOTE-LIST-VIEW.TABLE-HEADER.ADVISED-DATE',
                        translationKey:
                            'OUTGOING-GOODS.DELIVERY-NOTE-LIST-VIEW.TABLE-HEADER.ADVISED-DATE',
                    },
                    {
                        keyName: 'checkinDate',
                        label: 'OUTGOING-GOODS.DELIVERY-NOTE-LIST-VIEW.TABLE-HEADER.RECEIVED-DATE',
                        translationKey:
                            'OUTGOING-GOODS.DELIVERY-NOTE-LIST-VIEW.TABLE-HEADER.RECEIVED-DATE',
                    },
                    {
                        keyName: 'carrierNumberPlateIncoming',
                        label: 'SPEDITEUR.TABLE.HEADERS.PLATE-OUT',
                        translationKey: 'SPEDITEUR.TABLE.HEADERS.PLATE-OUT',
                    },
                    {
                        keyName: 'deliveryNoteBundleStatus',
                        label: 'OUTGOING-GOODS.DELIVERY-NOTE-LIST-VIEW.TABLE-HEADER.STATE',
                        translationKey:
                            'OUTGOING-GOODS.DELIVERY-NOTE-LIST-VIEW.TABLE-HEADER.STATE',
                    },
                    {
                        keyName: 'discrepancies',
                        label: 'OUTGOING-GOODS.DELIVERY-NOTE-LIST-VIEW.TABLE-HEADER.DISCREPANCIES',
                        translationKey:
                            'OUTGOING-GOODS.DELIVERY-NOTE-LIST-VIEW.TABLE-HEADER.DISCREPANCIES',
                    },
                    {
                        keyName: 'palletTickets',
                        label: 'OUTGOING-GOODS.DELIVERY-NOTE-LIST-VIEW.TABLE-HEADER.PALLET-TICKET',
                        translationKey:
                            'OUTGOING-GOODS.DELIVERY-NOTE-LIST-VIEW.TABLE-HEADER.PALLET-TICKET',
                    },
                ],
                showClosed: true,
                enableAdvancedFilters: true,
            },
            searchConfig: {
                validSortFields: [
                    'dateCreated',
                    'number',
                    'orderNumber',
                    'consignorName',
                    'consigneeName',
                    'carrierName',
                    'deliveryNoteBundleNumber',
                    'dateShipped',
                    'dateAnnounced',
                    'dateReceived',
                    'status',
                ],
                defaultSortField: 'dateCreated',
                defaultSortDirection: 'desc',
            },
            permissions: {
                canCreate: false,
                canUpdate: true,
                canDelete: false,
                canExport: true,
                canImport: false,
            },
            selectionConfig: {
                clearOnViewTypeSwitch: true,
            },
            headerActionConfig: {
                headerActions: [
                    {
                        id: 'scanWallet',
                        label: 'Scan Wallet',
                        translationKey: 'INCOMING-GOODS.BUTTONS.SCAN-WALLET',
                        icon: 'qr_code_scanner',
                        color: 'primary',
                        variant: 'raised',
                        cssClasses:
                            'fix-button-position tw-h-14 2xl:!tw-text-xl',
                        position: 'right',
                        order: 2,
                        visible: (item, selection) => true,
                        disabled: (item, selection) => false,
                        handler: async () => {
                            deps.actionsService.scanWallet();
                        },
                        tooltip: 'INCOMING-GOODS.BUTTONS.SCAN-WALLET',
                    },
                ],
            },
        };
    }
}
